import { STALE_TIME } from 'business/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HttpError, RQAPICall, RQAPICallWithData } from 'store/APIClient';
import { useDispatch } from 'store/hooks';
import { FieldInfo, TableSynopsis } from '../types';

/**
 * Get the list of all available metrics for an explore request
 */
export const useGetTableSynopsis = (table: string) =>
    useQuery({
        queryKey: ['table-table_synopsis', table],

        queryFn: async () => {
            const response = await RQAPICallWithData<TableSynopsis>({
                endpoint: `graph/table_synopsis/${table}`,
                method: 'GET',
            });
            return response.content.data;
        },

        staleTime: STALE_TIME,
    });

const getFieldSynopsis = async (table: string, fieldName: string) => {
    const response = await RQAPICallWithData<FieldInfo>({
        endpoint: `graph/table_synopsis/${table}/${fieldName}`,
        method: 'GET',
    });

    return response.content.data;
};

/**
 * Fetch the table fields details
 */
export const useGetFieldSynopsis = (table: string, fieldName: string) =>
    useQuery({
        queryKey: ['field_synopsis', table, fieldName],

        queryFn: () => getFieldSynopsis(table, fieldName),
    });

const toggleFieldVisibility = async ({
    table,
    name,
}: {
    table: string;
    name: string;
}) => {
    const response = await RQAPICall({
        endpoint: `graph/table/${table}/field/${name}/visibility`,
        method: 'PUT',
    });

    return response;
};

/**
 * Mutation for toggling the visibility of a field in the cellar.
 */
export const useToggleFieldVisibilityMutation = (
    tableName: string,
    fieldName: string,
    toggleVisibility: () => void,
): {
    mutate: ({ table, name }: { table: string; name: string }) => void;
} => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: toggleFieldVisibility,

        onSuccess: (data) => {
            toggleVisibility();
            queryClient.setQueryData(
                ['field_synopsis', tableName, fieldName],
                (oldFieldSynopsis: FieldInfo) => ({
                    ...oldFieldSynopsis,
                    isVisibleForBind: !oldFieldSynopsis.isVisibleForBind,
                }),
            );
            queryClient.invalidateQueries({
                queryKey: ['get_metrics'],
            });
            queryClient.invalidateQueries({
                queryKey: ['get_dimensions'],
            });
            queryClient.invalidateQueries({
                queryKey: ['get_filters'],
            });
            queryClient.invalidateQueries({
                queryKey: ['available_fields'],
            });
            dispatch.vision.notifyUser({
                message: data.content.message,
                snackType: 'success',
            });
        },

        onError: (error: HttpError) => {
            queryClient.invalidateQueries({
                queryKey: ['field_synopsis', tableName, fieldName],
            });
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
        },
    });
};
